import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ComponentOverlayLayer.module.css';
import { getSectionColor } from './sectionColors';

interface OverlayEntry {
  name: string;
  rect: DOMRect;
  section: string;
}

function measureOverlays(): OverlayEntry[] {
  const nodes = document.querySelectorAll<HTMLElement>('[data-overlay-name]');
  const entries: OverlayEntry[] = [];

  nodes.forEach(el => {
    const name = el.dataset.overlayName ?? '';
    const section = el.dataset.overlaySection ?? '';

    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const rect = range.getBoundingClientRect();

      // Skip zero-size rects (hidden or not yet rendered)
      if (rect.width === 0 && rect.height === 0) return;

      entries.push({ name, rect, section });
    } catch {
      // Ignore measurement errors for individual components
    }
  });

  return entries;
}

export function ComponentOverlayLayer() {
  const [mounted, setMounted] = useState(false);
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);
  const rafRef = useRef<number | null>(null);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setOverlays(measureOverlays());
    });
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Initial measurement
    scheduleUpdate();

    // Scroll listener (capture phase, passive)
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });

    // ResizeObserver on documentElement for viewport/layout changes
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(document.documentElement);

    // Debounced MutationObserver on the dynamic page wrapper to catch async/lazy hydration
    let mutationDebounce: ReturnType<typeof setTimeout> | null = null;
    const debouncedScheduleUpdate = () => {
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      mutationDebounce = setTimeout(scheduleUpdate, 100);
    };

    const mutationObserver = new MutationObserver(debouncedScheduleUpdate);
    const wrapper = document.getElementById('dynamic-page-main-tag-wrapper');
    if (wrapper) {
      mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: false });
    }

    return () => {
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [mounted, scheduleUpdate]);

  // Render nothing during SSR and initial hydration
  if (!mounted) return null;

  return (
    <div aria-hidden="true" className={styles.overlayLayer}>
      {overlays.map((entry, i) => {
        const color = getSectionColor(entry.section);
        const label = entry.section ? `${entry.name} · ${entry.section}` : entry.name;

        return (
          <div
            className={styles.componentBox}
            key={`${entry.name}-${i}`}
            style={{
              top: entry.rect.top,
              left: entry.rect.left,
              width: entry.rect.width,
              height: entry.rect.height,
              outline: `2px solid ${color}`,
            }}
          >
            <span className={styles.componentLabel} style={{ backgroundColor: color }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
